/**
 * Calendar-date helpers (#322).
 *
 * A CALENDAR DATE has no time and no timezone. A tournament on 16 September is
 * on 16 September in Vilnius, in London and in New York. The six `date` columns
 * are calendar dates: `tournaments.date_start`, `tournaments.date_end`,
 * `bouts.bout_date`, `bout_plan_items.bout_date`,
 * `tournament_weighins.weigh_in_date` and `athlete_records.birth_date`.
 *
 * An INSTANT is a moment — `created_at`, `registration_deadline`. Those stay in
 * `timestamptz`, travel as UTC and render in the browser's timezone. Do not use
 * anything in this file for an instant.
 *
 * Two rules, because there are two kinds of value:
 *
 *   calendar date -> 'YYYY-MM-DD' string, end to end, never converted
 *   instant       -> UTC in the database, browser-local on screen
 *
 * The trap this file exists to remove: `toISOString()` converts to UTC, so a
 * LOCAL Date turned into a day string returns YESTERDAY for any positive offset
 * in the evening. At UTC+3 that is every day after 21:00 — which is exactly
 * when a tournament runs.
 */

/** 'YYYY-MM-DD' for a Date, read in the BROWSER's timezone.
 *  The replacement for `d.toISOString().slice(0, 10)`. */
export function toLocalDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Today, in the browser's timezone. */
export function todayLocal(): string {
  return toLocalDay(new Date());
}

/** The earliest birth date that a form takes: 100 years back, as the
 *  `birthDate()` rule of the backend (`MAX_AGE_YEARS`). A fixed 1980 stopped
 *  the Masters who were born from 1976 to 1979 (logic review, 2026-10-15). */
export function birthDateMin(): string {
  return `${new Date().getFullYear() - 100}-01-01`;
}

/** Parse 'YYYY-MM-DD' as LOCAL midnight.
 *
 *  `new Date('2026-09-16')` is parsed as UTC midnight by the ECMAScript
 *  date-only form, so formatting it in a timezone behind UTC renders the 15th.
 *  Building the parts explicitly keeps the day the caller asked for. Use this
 *  only to hand a Date to a formatter — never to compare two calendar dates,
 *  which compare correctly as strings.
 */
export function parseDay(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Every calendar day from `startIso` to `endIso`, inclusive.
 *
 *  Built on local midnight and read back with `toLocalDay`, so no UTC
 *  conversion happens at any step. `setDate` handles month and year rollover
 *  and stays correct across a daylight-saving change. */
export function enumerateDays(startIso: string, endIso?: string | null): string[] {
  const start = String(startIso).slice(0, 10);
  if (!start) return [];
  const end = endIso ? String(endIso).slice(0, 10) : start;
  if (end < start) return [start];

  const out: string[] = [];
  const cur = parseDay(start);
  // 400 caps a malformed range; a real tournament is a handful of days.
  for (let guard = 0; guard < 400; guard++) {
    const day = toLocalDay(cur);
    out.push(day);
    if (day >= end) break;
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}
